import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PDFJSViewer } from '../../../components/pdf-viewer/PDFJSViewer';
import DocumentPagePdfJs from '../../document-wizard/shared-offer/DocumentPagePdfJs';
import { useDocumentRole } from '../../../shared/hooks/useDocumentRole/useDocumentRole';
import { DocumentSourceEnum } from '../../../components/document-wizard/document-editor/constants/EditorConstats';
import { env } from '../../../config/env';
import { useDocumentState } from '../../../contexts/DocumentContext';
import LoadingSpinner from '../../../components/loading-spinner';
import { DocumentPermissions } from '../../document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { useMutation, useQuery } from '@apollo/client';
import { mutations, queriesV2 } from '../../../api';
import { CounterpartyPermissionLevel, permissionLevelToBooleans } from '../../../types/counterparty-permissions';
import { getCurrentUsername } from '../../../api/queries';
import { DocumentStatusEnum } from '../../../shared/constants/document';
import { useAuth } from '../../../auth';
import { useDocumentWizardErrorNotifier } from '../../document-wizard/shared/hooks/use-document-wizard-error-notifier/use-document-wizard-error-notifier.hook';
import { DocumentEditorErrors } from '../../../components/document-wizard/document-editor/constants/EditorConstats';
import { useDeal } from '../../../contexts/DealContext';
import { fillPlaceholders, paths } from '../../../routing';
import {
  GetDealByIdVariables,
  GetDealTeamsByTeamIdData,
} from '../../../components/document-wizard/document-editor/DocumentEditorTypes';


interface Props {
  documentId: string;
  teamId: string;
  userPermissions: DocumentPermissions;
  hasCurrentTeamPossession: boolean;
  isDocumentInReview: boolean;
}

const UploadedDocumentsViewerContainer = ({
  documentId,
  userPermissions,
  hasCurrentTeamPossession,
  teamId,
  isDocumentInReview,
}: Props) => {
  const { isDocumentCreator } = useDocumentRole(documentId);
  const [{ user }] = useAuth();
  const navigate = useNavigate();
  const isLockedRef = useRef<boolean>(false);
  const [isDocumentLocked, setIsDocumentLocked] = useState(false);
  const userId = user.id;
  const { dealId } = useDeal();

  const notifyError = useDocumentWizardErrorNotifier();
  const { state, updateDocumentState } = useDocumentState();
  const generatedDocumentUrl = state.generatedDocumentPdfUrl;
  const importedDocumentUrl = state.importedAssetUrl;
  const documentStatus = state.status;
  const document = state.document;
  const documentContentType = !state.source ? null : state.source === DocumentSourceEnum.APP ? 'application/pdf' : state.contentType;
  const isDocumentEditable = state.isEditable;
  const signedPdfUrl = state.signedPdfUrl;
  const documentSource = state.source;

  // Determine the correct PDF based on document source and state
  let documentUrl: string | null = null;

  if (documentSource === DocumentSourceEnum.IMPORTED) {
    // States that use imported_asset
    if ([DocumentStatusEnum.UPLOADED, DocumentStatusEnum.DRAFT, DocumentStatusEnum.SHARED, DocumentStatusEnum.REVIEWING].includes(documentStatus as DocumentStatusEnum)) {
      documentUrl = importedDocumentUrl;
      if (!documentUrl) {
        throw new Error(`Imported document ${documentId} in ${documentStatus} state missing imported_asset`);
      }
    }
    // PREPARING_TO_SIGN and SIGNING use generated_lease_pdf (preview of what was sent)
    else if ([DocumentStatusEnum.PREPARING_TO_SIGN, DocumentStatusEnum.SIGNING].includes(documentStatus as DocumentStatusEnum)) {
      documentUrl = generatedDocumentUrl;
      // If URL is null, show loading spinner (PDF is still being generated)
      // Backend only returns URL once file is actually attached and ready
    }
    // EXECUTED uses signed_pdf or eversign_pdf (handled separately below at line 266)
    else if (documentStatus === DocumentStatusEnum.EXECUTED) {
      // documentUrl not needed - will use signedPdfUrl or eversignPdfUrl
      // Just set to null to avoid error, actual PDF loading handled in EXECUTED block below
      documentUrl = null;
    }
    // Unknown state for imported documents
    else {
      throw new Error(`Unknown status ${documentStatus} for imported document ${documentId}`);
    }
  }
  else if (documentSource === DocumentSourceEnum.APP) {
    // CKEditor docs always use generated_lease_pdf
    documentUrl = generatedDocumentUrl;
    if (!documentUrl) {
      throw new Error(`CKEditor document ${documentId} in ${documentStatus} state missing generated_lease_pdf`);
    }
  }

  const documentTitle = state.title;
  const eversignPDFUrl = state.eversignPdfUrl as string;
  const lastUpdated = state.lastUpdated;
  const isDocumentExecuted = [DocumentStatusEnum.EXECUTED].includes(documentStatus as DocumentStatusEnum);

  const [lockDocument, { loading: isLockLeaseDocumentLoading }] = useMutation(mutations.lockDocument);
  const [unlockDocument] = useMutation(mutations.unlockDocument);
  const [setCounterpartyPermissionLevelMutation] = useMutation(mutations.setCounterpartyPermissionLevel, {
    onCompleted: (data) => {
      const newLevel = data.setCounterpartyPermissionLevel.counterpartyPermissionLevel as CounterpartyPermissionLevel;
      // Update document state to keep it in sync with Apollo cache
      updateDocumentState({ counterpartyPermissionLevel: newLevel, ...permissionLevelToBooleans(newLevel) });
      // Emit custom event for cross-UI sync (WebViewer DOM dropdown ↔ slideouts)
      window.dispatchEvent(new CustomEvent('propdocs:permissionChanged', {
        detail: { type: 'level', value: newLevel }
      }));
    },
  });

  const { data: userData, loading: isUserDataLoading } = useQuery(getCurrentUsername, {
    fetchPolicy: 'network-only',
  });

  // Query for deal team data to get user lists for mentions
  const { loading: dealLoading, data: dealData } = useQuery<GetDealTeamsByTeamIdData, GetDealByIdVariables>(
    queriesV2.getDealTeamsByTeamId,
    {
      skip: !dealId,
      variables: {
        dealId: dealId,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  // Create user list for mentions (similar to DocumentEditor)
  const userList = useMemo(() => {
    if (dealLoading || !dealData) return [];

    const {
      getDealById: { team, receiverTeam, members: dealMembers, timelineMembers: dealTimelineMembers },
    } = dealData;
    const receiverTeamMembers = receiverTeam?.teamMembers || [];
    const bothTeamsMembers = [...team.teamMembers, ...receiverTeamMembers];
    const dealMembersIds = dealMembers.map(({ id }) => id);
    const teamsMembersWithAccessToDeal = bothTeamsMembers.filter(({ user }) => dealMembersIds.includes(user.id));

    const mappedTimelineMembers = dealTimelineMembers.map((member: any) => ({
      id: member.id,
      name: member.fullName,
      email: member.email,
    }));

    return mappedTimelineMembers;
  }, [dealLoading, dealData]);

  // Create current user's team members list for private mentions
  const currentUserTeamMembersList = useMemo(() => {
    if (dealLoading || !dealData) return [];

    const {
      getDealById: { team, receiverTeam, members: dealMembers },
    } = dealData;
    const receiverTeamMembers = receiverTeam?.teamMembers || [];
    const currentUserTeamList = isDocumentCreator ? team.teamMembers : receiverTeamMembers;
    const dealMembersIds = dealMembers.map(({ id }) => id);
    const teamsMembersWithAccessToDeal = currentUserTeamList.filter(({ user }) => dealMembersIds.includes(user.id));
    return teamsMembersWithAccessToDeal.map(({ user }) => ({ id: user.id, name: user.fullName, email: user.email }));
  }, [dealLoading, isDocumentCreator, dealData]);

  const [updateDocumentPdfWithFormValues] = useMutation(mutations.updateDocumentPdfWithFormValues);

  const navigateToDealDetails = useCallback(() => {
    const url = fillPlaceholders(paths.deals.detail, { teamId, dealId });
    navigate(url);
  }, [dealId, teamId, navigate]);

  const handleClose = useCallback(() => {
    navigateToDealDetails();
  }, [navigateToDealDetails]);

  const handleUnlock = useCallback(() => {
    if (isLockedRef.current) {
      unlockDocument({ variables: { documentId, userId, sessionActivities: [] } });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    // Check if counterparty (opposite team) has the document open
    // This prevents simultaneous editing conflicts
    const counterpartyHasDocument = isDocumentCreator && !hasCurrentTeamPossession;

    if (counterpartyHasDocument) {
      setIsDocumentLocked(true);
      notifyError({
        message: 'This document is currently being reviewed by your counterparty. You cannot edit until they finish.',
        pinned: true,
      });
    } else if (isDocumentEditable) {
      lockDocument({ variables: { documentId, userId } })
        .then(() => {
          isLockedRef.current = true;
        })
        .catch((err: any) => {
          // Lock failed - someone else has the document open
          setIsDocumentLocked(true);
          !isDocumentExecuted && notifyError({
            message: DocumentEditorErrors.IS_LOCKED.defaultMessage,
            pinned: true
          });
        });
    }

    return () => {
      if (isLockedRef.current && isDocumentEditable) {
        unlockDocument({
          variables: { documentId, userId, sessionActivities: [] },
          refetchQueries: [
            {
              query: queriesV2.getDocumentsByDeal,
              variables: {
                dealId,
              },
            },
          ],
        });
      }
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    window.addEventListener('beforeunload', handleUnlock);

    return () => {
      window.removeEventListener('beforeunload', handleUnlock);
    };
  }, []); // eslint-disable-line

  // Check if we're still loading necessary data
  // Note: For EXECUTED documents, documentUrl is null (we use signedPdfUrl/eversignPdfUrl instead)
  const isLoadingData =
    !documentContentType ||
    !documentStatus ||
    isUserDataLoading ||
    isLockLeaseDocumentLoading ||
    dealLoading ||
    (!documentUrl && !isDocumentExecuted); // documentUrl only required for non-executed documents

  if (isLoadingData) {
    return <LoadingSpinner />;
  }

  // Executed documents are read-only, so no annotation layer is built.
  if (isDocumentExecuted) {
    const executedPdfUrl = (signedPdfUrl || eversignPDFUrl) as string;

    // If signed PDF is not ready yet, show loading spinner
    if (!executedPdfUrl) {
      return <LoadingSpinner />;
    }

    // Convert relative Active Storage URLs to absolute URLs
    // Backend returns: /rails/active_storage/blobs/...
    // PDF.js needs: http://localhost:3000/rails/active_storage/blobs/...
    const absolutePdfUrl = executedPdfUrl?.startsWith('http')
      ? executedPdfUrl
      : `${env.API_URL}${executedPdfUrl}`;

    return (
      <PDFJSViewer
        pdfUrl={absolutePdfUrl}
        mode="read-only"
        initialAnnotations={[]}
        showControls={true}
        hideFormFieldsAndAnnotations={true}
      />
    );
  }

  // Every other status also renders through PDF.js.
  // Convert relative Active Storage URLs to absolute URLs
  const absolutePdfUrl = documentUrl?.startsWith('http')
    ? documentUrl
    : `${env.API_URL}${documentUrl}`;

  return (
    <DocumentPagePdfJs
      lastUpdated={lastUpdated || ''}
      documentTitle={documentTitle || 'Document'}
      isDocumentCreator={isDocumentCreator}
      documentId={documentId}
      permissions={userPermissions}
      handleClose={handleClose}
      pdfUrl={absolutePdfUrl}
      updateDocumentPdfWithFormValues={updateDocumentPdfWithFormValues as any}
      isDocumentLocked={isDocumentLocked}
      dealId={dealId}
      teamId={teamId}
      documentContentType={documentContentType}
      documentStatus={documentStatus}
      documentSource={DocumentSourceEnum.IMPORTED}
      hasCurrentTeamPossession={hasCurrentTeamPossession}
      canEdit={userPermissions.hasEditPermission}
      canComment={userPermissions.hasCommentPermission}
      isDocumentEditable={isDocumentEditable}
      isDocumentInReview={isDocumentInReview}
    />
  );
};

export default UploadedDocumentsViewerContainer;
